import React, { useRef, useEffect, useState, useCallback } from 'react';
import { shape, object, string, bool, func } from 'prop-types';

import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import SmallButton from '@johnlewispartnership/wtr-ingredients/ingredients/SmallButton';
import { TextField } from '@johnlewispartnership/wtr-ingredients/ingredients/Forms';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { flatSchema as addressSchema } from 'components/Address/util';
import AddressSearch from 'components/AddressSearch';
import CountrySelect from 'components/CountrySelect';

import styles from 'components/Address/AddressForm/AddressForm.scss';

import {
  line1Validator,
  line2Validator,
  line3Validator,
  townValidator,
  regionValidator,
} from './validator';
import { useFormHook } from './form-hook';
import { updatePostalCodeStatus } from './postcode-status';

const formDefaultPropSetJustSelectedFromAddressFinder = () => {};
const formDefaultPropPostalCodeCheck = () => Promise.resolve(null);

const Form = ({
  formContext,
  hasFoundAddress,
  setJustSelectedFromAddressFinder = formDefaultPropSetJustSelectedFromAddressFinder,
  initialValues: { country, isContactAddress },
  manualOnly,
  onSubmit,
  submitText,
  ukOnlyAddress = false,
  postalCodeCheck = formDefaultPropPostalCodeCheck,
}) => {
  const { errors: validationErrors, handleSubmit, register, trigger, watch } = formContext;
  const formValues = watch(['line1', 'line2', 'line3', 'town', 'region', 'postalCode', 'country']);
  const formValuesRef = useRef(formValues);
  const [isEditing, setEditing] = useState(false);

  const [state, setPlaceholders, setManualAddress, setPredictiveAddress] = useFormHook({
    country,
    hasFoundAddress,
    isEditing,
    manualOnly,
    formValuesRef,
  });

  const { placeholders, postcodeZipValidator, showCountrySelect, showSummary, showManualAddress } =
    state;

  const [postalCodeStatus, setPostalCodeStatus] = useState(null);

  const checkPostalCode = useCallback(() => {
    const currentFormValues = formValuesRef.current;
    const currentCountry = currentFormValues.country ? currentFormValues.country : country;

    updatePostalCodeStatus(
      currentFormValues.postalCode,
      currentCountry,
      postcodeZipValidator,
      postalCodeCheck,
      setPostalCodeStatus,
    );
  }, [country, postcodeZipValidator, postalCodeCheck]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    const handleSubmitAddressForm = event => {
      handleSubmit(onSubmit)();
    };

    document.addEventListener('submitAddressForm', handleSubmitAddressForm);

    return () => {
      document.removeEventListener('submitAddressForm', handleSubmitAddressForm);
    };
  }, [handleSubmit, onSubmit]);

  const handleEditOrManualEntry = async () => {
    setEditing(true);
    setPostalCodeStatus(null);
    await setManualAddress();
    if (formValues.line1) trigger(['line1', 'postalCode']);
    document.getElementById('line1').focus();
  };

  const predictiveAddressValidationError =
    (validationErrors?.line1 || validationErrors?.postalCode) &&
    !showManualAddress &&
    !hasFoundAddress
      ? 'Please enter an address'
      : '';

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPostalCodeStatus(null);
  }, [formValues.country]);

  const handleAddressSearchPostSelect = () => {
    setEditing(false);
    setPredictiveAddress();
    if (setJustSelectedFromAddressFinder) {
      setJustSelectedFromAddressFinder(true);
    }
    setTimeout(() => {
      const editAddressButton = document.getElementById('edit-address');
      if (editAddressButton) {
        editAddressButton.focus();
      }
    }, 0);
    checkPostalCode();
  };

  return (
    <>
      <div className={showCountrySelect ? null : 'hidden'} data-testid="country-container">
        <CountrySelect
          setPlaceholders={setPlaceholders}
          ukOnlyAddress={ukOnlyAddress}
          initialCountry={country}
          {...formContext}
        />
      </div>
      {showSummary && (
        <>
          <Typography
            styleAs="paragraphHeading"
            textStyle="heading-l"
            className={styles.addressSummaryHeading}
            noMargins
          >
            {isContactAddress ? 'Contact address' : 'Address finder'}
          </Typography>
          <div className={styles.addressSummary} data-testid="addressSummary">
            <Typography styleAs="paragraphHeading" textStyle="heading-l" noMargins data-cs-mask>
              {formValues.line1 && (
                <span className={styles.addressSummaryField}>{formValues.line1}</span>
              )}
              {formValues.line2 && (
                <span className={styles.addressSummaryField}>{formValues.line2}</span>
              )}
              {formValues.line3 && (
                <span className={styles.addressSummaryField}>{formValues.line3}</span>
              )}
              {formValues.town && (
                <span className={styles.addressSummaryField}>{formValues.town}</span>
              )}
              {formValues.region && (
                <span className={styles.addressSummaryField}>{formValues.region}</span>
              )}
              {formValues.postalCode && (
                <span className={styles.addressSummaryField}>{formValues.postalCode}</span>
              )}
            </Typography>
            <SmallButton
              id="edit-address"
              data-testid="edit-address-button"
              onClick={handleEditOrManualEntry}
              label="Edit"
              theme="secondary"
            />
          </div>
        </>
      )}
      {!showSummary && (
        <>
          <AddressSearch
            onCancel={handleEditOrManualEntry}
            dataTag="address-search"
            validationError={predictiveAddressValidationError}
            postSelect={handleAddressSearchPostSelect}
          />
          {!showManualAddress && (
            <SmallButton
              className={styles.manualAddressButton}
              data-test="manual-address-button"
              name="manualAddressButton"
              onClick={handleEditOrManualEntry}
              label="Enter address manually"
              theme="secondary"
            />
          )}
        </>
      )}
      <div className={showManualAddress ? '' : 'hidden'} data-testid="address-fields">
        <TextField
          autoComplete="address-line1"
          errorText={validationErrors?.line1?.message}
          id="line1"
          label={placeholders[0]}
          name="line1"
          defaultValue={formValues.line1}
          ref={register(line1Validator)}
          required
        />
        <TextField
          autoComplete="address-line2"
          errorText={validationErrors?.line2?.message}
          id="line2"
          label={placeholders[1]}
          name="line2"
          defaultValue={formValues.line2}
          indicateOptional
          ref={register(line2Validator)}
        />
        <TextField
          autoComplete="address-line3"
          errorText={validationErrors?.line3?.message}
          id="line3"
          label={placeholders[2]}
          name="line3"
          defaultValue={formValues.line3}
          indicateOptional
          ref={register(line3Validator)}
        />
        <TextField
          autoComplete="address-level2"
          errorText={validationErrors?.town?.message}
          id="town"
          label={placeholders[3]}
          name="town"
          defaultValue={formValues.town}
          indicateOptional
          ref={register(townValidator)}
        />
        <TextField
          autoComplete="address-level1"
          errorText={validationErrors?.region?.message}
          id="county"
          label={placeholders[4]}
          name="region"
          defaultValue={formValues.region}
          indicateOptional
          ref={register(regionValidator)}
        />
        <TextField
          autoCapitalize="characters"
          autoComplete="postal-code"
          errorText={validationErrors?.postalCode?.message}
          id="postalCode"
          label={placeholders[5]}
          name="postalCode"
          defaultValue={formValues.postalCode}
          ref={register(postcodeZipValidator)}
          onChange={checkPostalCode}
          required
        />
      </div>
      {isContactAddress && (
        <Typography
          styleAs="paragraphHeading"
          textStyle="heading-l"
          data-testid="isContactAddressText"
        >
          This will be the contact address used for all Waitrose communications you choose to
          receive, including myWaitrose.
        </Typography>
      )}
      {postalCodeStatus && postalCodeStatus}
      <Button
        data-test="submit-address-form"
        onClick={handleSubmit(onSubmit)}
        label={submitText}
        type="submit"
        width="full"
      />
    </>
  );
};

Form.propTypes = {
  formContext: object.isRequired,
  setJustSelectedFromAddressFinder: func,
  hasFoundAddress: bool.isRequired,
  initialValues: shape(addressSchema).isRequired,
  manualOnly: bool.isRequired,
  onSubmit: func.isRequired,
  submitText: string.isRequired,
  ukOnlyAddress: bool,
  postalCodeCheck: func,
};

export default Form;
